import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/takeUntil';
import { SidebarService } from '../../../services/side-bar.service';
import { AdminService } from '../../../services/admin.service';
import { ClothingService } from '../../../services/clothing.service';
import { ClothingErrorService } from '../../../services/clothing-error.service';
import { UserService } from '../../../services/user.service';
import {
  ClothingCatalogItem,
  ClothingCatalogItemList,
  ClothingCatalogItemPayload,
  ClothingConfiguredMarker,
  ClothingConfiguredMarkerPayload,
  ClothingErrorCode,
  ClothingMarker,
  ClothingMarkerNorm,
  ClothingMarkerNormPayload,
  ClothingMatrixCell,
  ClothingMatrixRow,
  ClothingOption,
  ClothingPersonalNorm,
  ClothingPersonalNormPayload,
  ClothingSettings,
  ClothingUserOption
} from '../../../models/clothing.model';
import { CLOTHING_MENU_OPTIONS } from './clothing.constants';
import {
  ClothingCatalogModalComponent,
  ClothingCatalogModalResult,
  CLOTHING_CATALOG_MODAL_ACTIONS
} from './clothing-catalog-modal.component';
import { ClothingMatrixAction } from './clothing-admin-matrix.component';
import {
  ClothingMarkerModalComponent,
  ClothingMarkerModalResult
} from './clothing-marker-modal.component';
import { ClothingMarkerAssignmentListItem } from './clothing-marker-assignment-panel.component';

interface ClothingMatrixData {
  items: ClothingCatalogItemList[];
  markers: ClothingMarker[];
  norms: ClothingMarkerNorm[];
}

interface ClothingNormIndex {
  [key: string]: ClothingMarkerNorm;
}

interface ClothingUsersListItem {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roles: string[];
  jobMarkerCode: string;
}

interface ClothingDepartmentMarkerOption extends ClothingOption {
  departmentMarkerCode?: string;
}

interface ClothingAssignmentUserPayload {
  user: Object;
  groupsWithPositions: Object[];
}

interface ClothingAdminSectionState {
  [key: string]: boolean;
}

interface ClothingCatalogNameIndex {
  [catalogItemId: string]: string;
}

interface ClothingSettingsPageData {
  settings: ClothingSettings;
  users: ClothingUsersListItem[];
  departments: ClothingDepartmentMarkerOption[];
  configuredMarkers: ClothingConfiguredMarker[];
  catalogItems: ClothingCatalogItemList[];
  catalogNameIndex: ClothingCatalogNameIndex;
  markers: ClothingMarker[];
  rows: ClothingMatrixRow[];
  storekeeperOptions: ClothingUserOption[];
  userOptions: ClothingUserOption[];
}

interface ClothingMarkerReloadData {
  departments: ClothingDepartmentMarkerOption[];
  configuredMarkers: ClothingConfiguredMarker[];
  catalogItems: ClothingCatalogItemList[];
  catalogNameIndex: ClothingCatalogNameIndex;
  markers: ClothingMarker[];
  rows: ClothingMatrixRow[];
}

type ClothingSettingsLoadTuple = [
  ClothingSettings,
  ClothingDepartmentMarkerOption[],
  ClothingConfiguredMarker[],
  ClothingCatalogItemList[],
  ClothingUsersListItem[]
];

type ClothingMarkerReloadTuple = [
  ClothingDepartmentMarkerOption[],
  ClothingConfiguredMarker[],
  ClothingCatalogItemList[]
];

@Component({
  selector: 'app-clothing-settings',
  templateUrl: './clothing-settings.component.html',
  styleUrls: ['./clothing-settings.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminClothingSettingsComponent implements OnInit, OnDestroy {
  settings: ClothingSettings = this.createDefaultSettings();
  configuredMarkers: ClothingConfiguredMarker[] = [];
  personalNorms: ClothingPersonalNorm[] = [];
  draftPersonalNorm: ClothingPersonalNormPayload = this.createPersonalNormDraft();
  catalogItems: ClothingCatalogItemList[] = [];
  catalogNameIndex: ClothingCatalogNameIndex = {};
  users: ClothingUsersListItem[] = [];
  departments: ClothingDepartmentMarkerOption[] = [];
  storekeeperOptions: ClothingUserOption[] = [];
  userOptions: ClothingUserOption[] = [];
  selectedPersonalNormUserId = '';
  markers: ClothingMarker[] = [];
  rows: ClothingMatrixRow[] = [];
  errorMessage = '';
  successMessageKey = '';
  editingCellKey = '';
  editingCellValue = 0;
  savingCellKey = '';
  isPageLoading = false;
  isMatrixLoading = false;
  isSettingsSaving = false;
  isMarkerSaving = false;
  isAssignmentSaving = false;
  isPersonalNormsLoading = false;
  isPersonalNormSaving = false;
  isCatalogSaving = false;
  sectionState: ClothingAdminSectionState = this.createSectionState();
  selectedDepartmentMarkerIndex = 0;
  selectedUserMarkerIndex = 0;
  pendingDepartmentMarkerCode = '';
  pendingUserMarkerCode = '';
  private destroy$ = new Subject<void>();
  private modalRefs: NgbModalRef[] = [];

  constructor(
    private adminService: AdminService,
    private changeDetectorRef: ChangeDetectorRef,
    private clothingService: ClothingService,
    private clothingErrorService: ClothingErrorService,
    private modalService: NgbModal,
    private sidebarService: SidebarService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.sidebarService.setSelectedMenuOption(CLOTHING_MENU_OPTIONS.settings);
    this.loadPage();
  }

  ngOnDestroy() {
    this.dismissOpenModals();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasMarkers(): boolean {
    return this.markers.length > 0;
  }

  get hasRows(): boolean {
    return this.rows.length > 0;
  }

  get footerColspan(): number {
    return this.markers.length + 1;
  }

  get departmentAssignmentMarkers(): ClothingConfiguredMarker[] {
    return this.filterConfiguredMarkers('DEPARTMENT');
  }

  get userAssignmentMarkers(): ClothingConfiguredMarker[] {
    return this.filterConfiguredMarkers('JOB');
  }

  get officeMarkerOptions(): ClothingOption[] {
    return this.departmentAssignmentMarkers.map((marker) => ({
      id: marker.markerCode,
      name: marker.markerCode
    }));
  }

  get currentDepartmentMarkerCode(): string {
    return this.resolveCurrentMarkerCode(
      this.departmentAssignmentMarkers,
      this.selectedDepartmentMarkerIndex
    );
  }

  get currentUserMarkerCode(): string {
    return this.resolveCurrentMarkerCode(
      this.userAssignmentMarkers,
      this.selectedUserMarkerIndex
    );
  }

  get departmentAssignmentItems(): ClothingMarkerAssignmentListItem[] {
    return this.departments.map((department) => this.mapDepartmentAssignmentItem(department));
  }

  get userAssignmentItems(): ClothingMarkerAssignmentListItem[] {
    return this.users.map((user) => this.mapUserAssignmentItem(user));
  }

  openCreateCatalogModal() {
    var modalRef = this.createCatalogModal();

    this.clearMessages();
    modalRef.componentInstance.setCreateMode();
    this.handleCatalogModal(modalRef, '');
  }

  openCreateMarkerModal() {
    var modalRef = this.trackModalRef(this.modalService.open(ClothingMarkerModalComponent, {
      backdrop: 'static'
    }));

    modalRef.result.then(
      (result: ClothingMarkerModalResult) => this.handleCreateMarkerResult(result),
      () => null
    );
  }

  openEditCatalogModal(itemId: string) {
    this.clearMessages();
    this.clothingService.getCatalogItem(itemId)
      .takeUntil(this.destroy$)
      .subscribe(
        (item) => this.handleCatalogItemLoaded(item),
        (error: HttpErrorResponse) => this.showHttpError(error)
      );
  }

  saveSettings() {
    this.clearMessages();
    this.isSettingsSaving = true;
    this.clothingService.updateSettings(this.createSettingsPayload(this.settings))
      .takeUntil(this.destroy$)
      .subscribe(
        (settings) => this.handleSettingsSaved(settings),
        (error: HttpErrorResponse) => this.handleSettingsFailed(error)
      );
  }

  selectPreviousDepartmentMarker() {
    this.selectedDepartmentMarkerIndex = this.moveMarkerIndex(
      this.selectedDepartmentMarkerIndex,
      this.departmentAssignmentMarkers,
      -1
    );
    this.markForCheck();
  }

  selectNextDepartmentMarker() {
    this.selectedDepartmentMarkerIndex = this.moveMarkerIndex(
      this.selectedDepartmentMarkerIndex,
      this.departmentAssignmentMarkers,
      1
    );
    this.markForCheck();
  }

  selectPreviousUserMarker() {
    this.selectedUserMarkerIndex = this.moveMarkerIndex(
      this.selectedUserMarkerIndex,
      this.userAssignmentMarkers,
      -1
    );
    this.markForCheck();
  }

  selectNextUserMarker() {
    this.selectedUserMarkerIndex = this.moveMarkerIndex(
      this.selectedUserMarkerIndex,
      this.userAssignmentMarkers,
      1
    );
    this.markForCheck();
  }

  assignDepartmentMarker(departmentId: string) {
    var markerCode = this.resolveDepartmentAssignmentCode(departmentId);

    if (!this.canSaveAssignment(this.currentDepartmentMarkerCode)) {
      return;
    }
    this.saveDepartmentAssignment(departmentId, markerCode);
  }

  assignUserMarker(userId: string) {
    var markerCode = this.resolveUserAssignmentCode(userId);

    if (!this.canSaveAssignment(this.currentUserMarkerCode)) {
      return;
    }
    this.saveUserAssignment(userId, markerCode);
  }

  saveConfiguredMarker(marker: ClothingConfiguredMarker) {
    this.clearMessages();
    this.isMarkerSaving = true;
    this.clothingService.updateConfiguredMarker(marker.id, this.createMarkerPayload(marker))
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.reloadMarkerData('CLOTHING_MARKER_SAVED'),
        (error: HttpErrorResponse) => this.handleMarkerFailed(error)
      );
  }

  deleteConfiguredMarker(markerId: string) {
    this.clearMessages();
    this.isMarkerSaving = true;
    this.clothingService.deleteConfiguredMarker(markerId)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.reloadMarkerData('CLOTHING_MARKER_DELETED'),
        (error: HttpErrorResponse) => this.handleMarkerFailed(error)
      );
  }

  selectPersonalNormUser(userId: string) {
    this.selectedPersonalNormUserId = userId;
    this.personalNorms = [];
    this.resetPersonalNormDraft(userId);
    this.loadSelectedPersonalNorms();
  }

  createPersonalNorm() {
    if (!this.isPersonalNormDraftValid()) {
      return;
    }
    this.clearMessages();
    this.isPersonalNormSaving = true;
    this.clothingService.createPersonalNorm(this.createPersonalNormPayload(this.draftPersonalNorm))
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.reloadPersonalNorms('CLOTHING_PERSONAL_NORM_SAVED'),
        (error: HttpErrorResponse) => this.handlePersonalNormFailed(error)
      );
  }

  savePersonalNorm(norm: ClothingPersonalNorm) {
    this.clearMessages();
    this.isPersonalNormSaving = true;
    this.clothingService.updatePersonalNorm(norm.id, this.createPersonalNormPayload(norm))
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.reloadPersonalNorms('CLOTHING_PERSONAL_NORM_SAVED'),
        (error: HttpErrorResponse) => this.handlePersonalNormFailed(error)
      );
  }

  deletePersonalNorm(normId: string) {
    this.clearMessages();
    this.isPersonalNormSaving = true;
    this.clothingService.deletePersonalNorm(normId)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.reloadPersonalNorms('CLOTHING_PERSONAL_NORM_DELETED'),
        (error: HttpErrorResponse) => this.handlePersonalNormFailed(error)
      );
  }

  startCellEdit(action: ClothingMatrixAction) {
    this.clearMessages();
    this.editingCellKey = this.createCellKey(action.row.item.id, action.cell.key);
    this.editingCellValue = action.cell.quantity;
    this.markForCheck();
  }

  saveCell(action: ClothingMatrixAction) {
    var quantity = Number(this.editingCellValue);

    this.clearMessages();
    this.savingCellKey = this.createCellKey(action.row.item.id, action.cell.key);
    this.getCellSaveRequest(action.row.item.id, action.cell, quantity)
      .takeUntil(this.destroy$)
      .subscribe(
        (norm) => this.handleCellSaved(action.cell, norm),
        (error: HttpErrorResponse) => this.handleCellSaveFailed(error)
      );
  }

  cancelCellEdit() {
    this.editingCellKey = '';
    this.editingCellValue = 0;
    this.markForCheck();
  }

  isEditingCell(row: ClothingMatrixRow, cell: ClothingMatrixCell): boolean {
    return this.editingCellKey === this.createCellKey(row.item.id, cell.key);
  }

  isSavingCell(row: ClothingMatrixRow, cell: ClothingMatrixCell): boolean {
    return this.savingCellKey === this.createCellKey(row.item.id, cell.key);
  }

  trackByMarker(index: number, marker: ClothingMarker): string {
    return marker.markerType + '::' + marker.markerCode;
  }

  trackByRow(index: number, row: ClothingMatrixRow): string {
    return row.item.id;
  }

  trackByCell(index: number, cell: ClothingMatrixCell): string {
    return cell.key;
  }

  toggleSection(sectionKey: string) {
    this.sectionState[sectionKey] = !this.isSectionExpanded(sectionKey);
    this.markForCheck();
  }

  isSectionExpanded(sectionKey: string): boolean {
    return !!this.sectionState[sectionKey];
  }

  getSectionIcon(sectionKey: string): string {
    if (this.isSectionExpanded(sectionKey)) {
      return 'fa fa-chevron-up';
    }
    return 'fa fa-chevron-down';
  }

  private loadPage() {
    this.isPageLoading = true;
    this.isMatrixLoading = true;
    Observable.forkJoin(
      this.loadSettingsOrDefault(),
      this.loadDepartments(),
      this.clothingService.getConfiguredMarkers(),
      this.clothingService.getCatalogItems(false),
      this.loadUsers()
    )
      .switchMap((response: ClothingSettingsLoadTuple) => this.loadPageData(response))
      .takeUntil(this.destroy$)
      .subscribe(
        (data) => this.handlePageLoaded(data),
        (error: HttpErrorResponse) => this.handlePageFailed(error)
      );
  }

  private loadSettingsOrDefault(): Observable<ClothingSettings> {
    return this.clothingService.getSettings()
      .catch((error: HttpErrorResponse) => this.resolveSettingsFallback(error));
  }

  private resolveSettingsFallback(error: HttpErrorResponse): Observable<ClothingSettings> {
    if (!this.isSettingsNotConfigured(error)) {
      return Observable.throw(error);
    }
    return Observable.of(this.createDefaultSettings());
  }

  private isSettingsNotConfigured(error: HttpErrorResponse): boolean {
    var clothingError = this.clothingErrorService.parseClothingError(error);

    if (!clothingError) {
      return false;
    }
    if (clothingError.errorCode === ClothingErrorCode.AdminSettingsNotConfigured) {
      return true;
    }
    return clothingError.errorCode === ClothingErrorCode.SettingsNotConfigured;
  }

  private loadUsers(): Observable<ClothingUsersListItem[]> {
    return this.adminService.getAllFullUsers()
      .map((response: HttpResponse<ClothingUsersListItem[]>) => response.body || [])
      .map((users) => this.normalizeUsers(users));
  }

  private loadDepartments(): Observable<ClothingDepartmentMarkerOption[]> {
    return this.adminService.getGroupsWithDepartmentMarker()
      .map((response: HttpResponse<ClothingDepartmentMarkerOption[]>) => response.body || [])
      .map((departments) => this.sortByName(this.filterDepartments(departments)));
  }

  private loadPageData(response: ClothingSettingsLoadTuple): Observable<ClothingSettingsPageData> {
    return this.loadMatrixData(response[3], response[2])
      .map((matrixData) => this.createPageData(response, matrixData));
  }

  private createPageData(
    response: ClothingSettingsLoadTuple,
    matrixData: ClothingMatrixData
  ): ClothingSettingsPageData {
    var configuredMarkers = this.sortConfiguredMarkers(response[2]);
    var catalogItems = response[3];
    var users = this.sortUsersList(response[4]);

    return {
      settings: this.createSettingsModel(response[0]),
      users: users,
      departments: response[1],
      configuredMarkers: configuredMarkers,
      catalogItems: catalogItems,
      catalogNameIndex: this.createCatalogNameIndex(catalogItems),
      markers: matrixData.markers,
      rows: this.createMatrixRows(matrixData.items, matrixData.markers, matrixData.norms),
      storekeeperOptions: this.extractStorekeepers(response[4]),
      userOptions: this.sortUsers(this.mapUsers(users))
    };
  }

  private handlePageLoaded(data: ClothingSettingsPageData) {
    this.settings = data.settings;
    this.users = data.users;
    this.departments = data.departments;
    this.configuredMarkers = data.configuredMarkers;
    this.catalogItems = data.catalogItems;
    this.catalogNameIndex = data.catalogNameIndex;
    this.storekeeperOptions = data.storekeeperOptions;
    this.userOptions = data.userOptions;
    this.markers = data.markers;
    this.rows = data.rows;
    this.isPageLoading = false;
    this.isMatrixLoading = false;
    this.isAssignmentSaving = false;
    this.syncMarkerSelection();
    this.cancelCellEdit();
  }

  private handlePageFailed(error: HttpErrorResponse) {
    this.isPageLoading = false;
    this.isMatrixLoading = false;
    this.isAssignmentSaving = false;
    this.showHttpError(error);
  }

  private handleSettingsSaved(settings: ClothingSettings) {
    this.settings = this.createSettingsModel(settings);
    this.isSettingsSaving = false;
    this.successMessageKey = 'CLOTHING_SETTINGS_SAVED';
    this.markForCheck();
  }

  private handleSettingsFailed(error: HttpErrorResponse) {
    this.isSettingsSaving = false;
    this.showHttpError(error);
  }

  private reloadMarkerData(successMessageKey: string) {
    this.isMarkerSaving = true;
    this.isMatrixLoading = true;
    Observable.forkJoin(
      this.loadDepartments(),
      this.clothingService.getConfiguredMarkers(),
      this.clothingService.getCatalogItems(false)
    )
      .switchMap((response: ClothingMarkerReloadTuple) => this.loadMarkerReloadData(response))
      .takeUntil(this.destroy$)
      .subscribe(
        (data) => this.handleMarkerReloaded(data, successMessageKey),
        (error: HttpErrorResponse) => this.handleMarkerFailed(error)
      );
  }

  private loadMarkerReloadData(
    response: ClothingMarkerReloadTuple
  ): Observable<ClothingMarkerReloadData> {
    return this.loadMatrixData(response[2], response[1])
      .map((matrixData) => this.createMarkerReloadData(response, matrixData));
  }

  private createMarkerReloadData(
    response: ClothingMarkerReloadTuple,
    matrixData: ClothingMatrixData
  ): ClothingMarkerReloadData {
    var configuredMarkers = this.sortConfiguredMarkers(response[1]);

    return {
      departments: response[0],
      configuredMarkers: configuredMarkers,
      catalogItems: response[2],
      catalogNameIndex: this.createCatalogNameIndex(response[2]),
      markers: matrixData.markers,
      rows: this.createMatrixRows(matrixData.items, matrixData.markers, matrixData.norms)
    };
  }

  private handleMarkerReloaded(data: ClothingMarkerReloadData, successMessageKey: string) {
    this.departments = data.departments;
    this.configuredMarkers = data.configuredMarkers;
    this.catalogItems = data.catalogItems;
    this.catalogNameIndex = data.catalogNameIndex;
    this.markers = data.markers;
    this.rows = data.rows;
    this.syncMarkerSelection();
    this.isMarkerSaving = false;
    this.isMatrixLoading = false;
    this.successMessageKey = successMessageKey;
    this.cancelCellEdit();
  }

  private handleMarkerFailed(error: HttpErrorResponse) {
    this.isMarkerSaving = false;
    this.isMatrixLoading = false;
    this.showHttpError(error);
  }

  private loadSelectedPersonalNorms() {
    if (!this.selectedPersonalNormUserId.length) {
      this.markForCheck();
      return;
    }
    this.isPersonalNormsLoading = true;
    this.clothingService.getPersonalNormsByUser(this.selectedPersonalNormUserId)
      .takeUntil(this.destroy$)
      .subscribe(
        (norms) => this.handlePersonalNormsLoaded(norms),
        (error: HttpErrorResponse) => this.handlePersonalNormLoadFailed(error)
      );
  }

  private handlePersonalNormsLoaded(norms: ClothingPersonalNorm[]) {
    this.personalNorms = this.normalizePersonalNorms(norms);
    this.isPersonalNormsLoading = false;
    this.markForCheck();
  }

  private handlePersonalNormLoadFailed(error: HttpErrorResponse) {
    this.isPersonalNormsLoading = false;
    this.showHttpError(error);
  }

  private reloadPersonalNorms(successMessageKey: string) {
    this.resetPersonalNormDraft(this.selectedPersonalNormUserId);
    this.isPersonalNormSaving = false;
    this.successMessageKey = successMessageKey;
    this.loadSelectedPersonalNorms();
  }

  private handlePersonalNormFailed(error: HttpErrorResponse) {
    this.isPersonalNormSaving = false;
    this.showHttpError(error);
  }

  private loadMatrix() {
    this.isMatrixLoading = true;
    Observable.forkJoin(
      this.clothingService.getCatalogItems(false),
      this.clothingService.getConfiguredMarkers()
    )
      .switchMap((response) => this.loadMatrixData(response[0], response[1]))
      .takeUntil(this.destroy$)
      .subscribe(
        (data) => this.handleMatrixLoaded(data),
        (error: HttpErrorResponse) => this.handleMatrixLoadFailed(error)
      );
  }

  private loadMatrixData(
    items: ClothingCatalogItemList[],
    markers: ClothingConfiguredMarker[]
  ): Observable<ClothingMatrixData> {
    var matrixMarkers = this.createMatrixMarkers(markers);

    if (!matrixMarkers.length) {
      return Observable.of(this.createMatrixData(items, matrixMarkers, []));
    }
    return this.loadNormsByMarkers(matrixMarkers)
      .map((norms) => this.createMatrixData(items, matrixMarkers, norms));
  }

  private loadNormsByMarkers(markers: ClothingMarker[]): Observable<ClothingMarkerNorm[]> {
    var requests = markers.map((marker) => this.loadMarkerNorms(marker));

    return Observable.forkJoin(requests)
      .map((groups) => this.flattenNormGroups(groups));
  }

  private loadMarkerNorms(marker: ClothingMarker): Observable<ClothingMarkerNorm[]> {
    return this.clothingService.getNormsByMarker(marker.markerCode)
      .map((norms) => this.filterMarkerNorms(norms, marker));
  }

  private createMatrixData(
    items: ClothingCatalogItemList[],
    markers: ClothingMarker[],
    norms: ClothingMarkerNorm[]
  ): ClothingMatrixData {
    return {
      items: items,
      markers: markers,
      norms: norms
    };
  }

  private flattenNormGroups(groups: ClothingMarkerNorm[][]): ClothingMarkerNorm[] {
    var norms: ClothingMarkerNorm[] = [];

    groups.forEach((group) => group.forEach((norm) => norms.push(norm)));
    return norms;
  }

  private filterMarkerNorms(
    norms: ClothingMarkerNorm[],
    marker: ClothingMarker
  ): ClothingMarkerNorm[] {
    return norms.filter((norm) => this.isSameMarker(norm, marker));
  }

  private isSameMarker(norm: ClothingMarkerNorm, marker: ClothingMarker): boolean {
    return norm.markerCode === marker.markerCode && norm.markerType === marker.markerType;
  }

  private handleMatrixLoaded(data: ClothingMatrixData) {
    this.markers = data.markers;
    this.rows = this.createMatrixRows(data.items, data.markers, data.norms);
    this.isMatrixLoading = false;
    this.cancelCellEdit();
    this.markForCheck();
  }

  private handleMatrixLoadFailed(error: HttpErrorResponse) {
    this.isMatrixLoading = false;
    this.showHttpError(error);
  }

  private createMatrixRows(
    items: ClothingCatalogItemList[],
    markers: ClothingMarker[],
    norms: ClothingMarkerNorm[]
  ): ClothingMatrixRow[] {
    var normIndex = this.createNormIndex(norms);

    return items.map((item) => this.createMatrixRow(item, markers, normIndex));
  }

  private createMatrixRow(
    item: ClothingCatalogItemList,
    markers: ClothingMarker[],
    normIndex: ClothingNormIndex
  ): ClothingMatrixRow {
    return {
      item: item,
      cells: markers.map((marker) => this.createMatrixCell(item.id, marker, normIndex))
    };
  }

  private createNormIndex(norms: ClothingMarkerNorm[]): ClothingNormIndex {
    var index: ClothingNormIndex = {};

    norms.forEach((norm) => index[this.createNormKey(norm.catalogItemId, norm)] = norm);
    return index;
  }

  private createMatrixCell(
    itemId: string,
    marker: ClothingMarker,
    normIndex: ClothingNormIndex
  ): ClothingMatrixCell {
    var norm = normIndex[this.createNormKey(itemId, marker)];

    return {
      key: this.createMarkerKey(marker),
      markerCode: marker.markerCode,
      markerType: marker.markerType,
      quantity: this.resolveCellQuantity(norm),
      normId: this.resolveNormId(norm),
      active: this.resolveNormActive(norm)
    };
  }

  private resolveCellQuantity(norm: ClothingMarkerNorm): number {
    if (!norm || !norm.active) {
      return 0;
    }
    return norm.quantity;
  }

  private resolveNormId(norm: ClothingMarkerNorm): string {
    if (!norm || !norm.id) {
      return '';
    }
    return norm.id;
  }

  private resolveNormActive(norm: ClothingMarkerNorm): boolean {
    if (!norm) {
      return false;
    }
    return norm.active;
  }

  private createMarkerKey(marker: ClothingMarker): string {
    return marker.markerType + '::' + marker.markerCode;
  }

  private createNormKey(itemId: string, marker: ClothingMarker): string {
    return itemId + '::' + this.createMarkerKey(marker);
  }

  private createCellKey(itemId: string, markerKey: string): string {
    return itemId + '::' + markerKey;
  }

  private createSettingsModel(settings: ClothingSettings): ClothingSettings {
    if (!settings) {
      return this.createDefaultSettings();
    }
    return this.createSettingsPayload(settings);
  }

  private createSettingsPayload(settings: ClothingSettings): ClothingSettings {
    return {
      storekeeperUserId: this.normalizeText(settings.storekeeperUserId),
      ceoUserId: this.normalizeText(settings.ceoUserId),
      officeDepartmentMarkerCode: this.normalizeText(settings.officeDepartmentMarkerCode),
      planfixDepartmentMarkerFieldId: this.normalizeNumber(settings.planfixDepartmentMarkerFieldId),
      planfixJobMarkerFieldId: this.normalizeNumber(settings.planfixJobMarkerFieldId),
      planfixEscalationParentTaskId: this.normalizeNumber(settings.planfixEscalationParentTaskId),
      summerStartMonthDay: this.normalizeText(settings.summerStartMonthDay),
      summerEndMonthDay: this.normalizeText(settings.summerEndMonthDay),
      outOfStockExtensionMonths: this.normalizeNumber(settings.outOfStockExtensionMonths),
      refusalThreshold: this.normalizeNumber(settings.refusalThreshold),
      refusalWindowMonths: this.normalizeNumber(settings.refusalWindowMonths),
      headOverdueAfterExpiryDays: this.normalizeNumber(settings.headOverdueAfterExpiryDays),
      active: !!settings.active
    };
  }

  private createMatrixMarkers(markers: ClothingConfiguredMarker[]): ClothingMarker[] {
    if (!markers || !markers.length) {
      return [];
    }
    return markers.map((marker) => ({
      markerCode: marker.markerCode,
      markerType: marker.markerType
    }));
  }

  private createDefaultSettings(): ClothingSettings {
    return {
      storekeeperUserId: '',
      ceoUserId: '',
      officeDepartmentMarkerCode: '',
      planfixDepartmentMarkerFieldId: 0,
      planfixJobMarkerFieldId: 0,
      planfixEscalationParentTaskId: 0,
      summerStartMonthDay: '',
      summerEndMonthDay: '',
      outOfStockExtensionMonths: 0,
      refusalThreshold: 0,
      refusalWindowMonths: 0,
      headOverdueAfterExpiryDays: 0,
      active: true
    };
  }

  private createSectionState(): ClothingAdminSectionState {
    return {
      generalSettings: false,
      normsMatrix: true
    };
  }

  private createMarkerPayload(
    source: ClothingConfiguredMarkerPayload
  ): ClothingConfiguredMarkerPayload {
    return {
      markerCode: this.normalizeText(source.markerCode),
      markerType: this.normalizeText(source.markerType),
      active: source.active !== false
    };
  }

  private createPersonalNormDraft(): ClothingPersonalNormPayload {
    return {
      userId: '',
      catalogItemId: '',
      quantity: 1
    };
  }

  private resetPersonalNormDraft(userId: string) {
    this.draftPersonalNorm = this.createPersonalNormDraft();
    this.draftPersonalNorm.userId = userId;
  }

  private createPersonalNormPayload(
    source: ClothingPersonalNormPayload
  ): ClothingPersonalNormPayload {
    return {
      userId: this.selectedPersonalNormUserId,
      catalogItemId: this.normalizeText(source.catalogItemId),
      quantity: this.normalizePositiveNumber(source.quantity)
    };
  }

  private isPersonalNormDraftValid(): boolean {
    if (!this.selectedPersonalNormUserId.length) {
      return false;
    }
    if (!this.normalizeText(this.draftPersonalNorm.catalogItemId).length) {
      return false;
    }
    return this.normalizePositiveNumber(this.draftPersonalNorm.quantity) > 0;
  }

  private normalizePersonalNorms(norms: ClothingPersonalNorm[]): ClothingPersonalNorm[] {
    return norms.map((norm) => ({
      id: norm.id,
      userId: norm.userId,
      catalogItemId: norm.catalogItemId,
      quantity: this.normalizePositiveNumber(norm.quantity),
      active: norm.active,
      createdAt: norm.createdAt,
      updatedAt: norm.updatedAt
    }));
  }

  private mapUsers(items: ClothingUsersListItem[]): ClothingUserOption[] {
    return items.map((item) => ({
      id: item.id,
      fullName: this.buildFullName(item)
    }));
  }

  private normalizeUsers(items: ClothingUsersListItem[]): ClothingUsersListItem[] {
    return items.map((item) => this.normalizeUser(item));
  }

  private normalizeUser(item: ClothingUsersListItem): ClothingUsersListItem {
    return {
      id: item.id,
      firstName: item.firstName,
      lastName: item.lastName,
      fullName: this.buildFullName(item),
      roles: item.roles || [],
      jobMarkerCode: this.normalizeText(item.jobMarkerCode)
    };
  }

  private extractStorekeepers(items: ClothingUsersListItem[]): ClothingUserOption[] {
    return this.sortUsers(this.mapUsers(this.filterStorekeepers(items)));
  }

  private filterStorekeepers(items: ClothingUsersListItem[]): ClothingUsersListItem[] {
    return items.filter((item) => this.hasStorekeeperRole(item.roles));
  }

  private hasStorekeeperRole(roles: string[]): boolean {
    if (!roles || !roles.length) {
      return false;
    }
    if (roles.indexOf('STOREKEEPER') !== -1) {
      return true;
    }
    return roles.indexOf('ROLE_STOREKEEPER') !== -1;
  }

  private buildFullName(item: ClothingUsersListItem): string {
    if (item.fullName && item.fullName.length) {
      return item.fullName;
    }
    return (item.lastName + ' ' + item.firstName).trim();
  }

  private sortByName<T extends ClothingOption>(items: T[]): T[] {
    return items.slice().sort((left, right) => {
      if (left.name < right.name) {
        return -1;
      }
      if (left.name > right.name) {
        return 1;
      }
      return 0;
    });
  }

  private sortUsers(items: ClothingUserOption[]): ClothingUserOption[] {
    return items.slice().sort((left, right) => {
      if (left.fullName < right.fullName) {
        return -1;
      }
      if (left.fullName > right.fullName) {
        return 1;
      }
      return 0;
    });
  }

  private sortUsersList(items: ClothingUsersListItem[]): ClothingUsersListItem[] {
    return items.slice().sort((left, right) => {
      if (left.fullName < right.fullName) {
        return -1;
      }
      if (left.fullName > right.fullName) {
        return 1;
      }
      return 0;
    });
  }

  private sortConfiguredMarkers(
    markers: ClothingConfiguredMarker[]
  ): ClothingConfiguredMarker[] {
    return markers.slice().sort((left, right) => {
      if (left.markerCode < right.markerCode) {
        return -1;
      }
      if (left.markerCode > right.markerCode) {
        return 1;
      }
      if (left.markerType < right.markerType) {
        return -1;
      }
      if (left.markerType > right.markerType) {
        return 1;
      }
      return 0;
    });
  }

  private filterConfiguredMarkers(markerType: string): ClothingConfiguredMarker[] {
    return this.configuredMarkers.filter((marker) => this.isVisibleConfiguredMarker(marker, markerType));
  }


  private isVisibleConfiguredMarker(
    marker: ClothingConfiguredMarker,
    markerType: string
  ): boolean {
    if (!marker.active) {
      return false;
    }
    return marker.markerType === markerType;
  }

  private createConfiguredMarkerPayload(
    markerCode: string,
    markerType: string
  ): ClothingConfiguredMarkerPayload {
    return {
      markerCode: markerCode,
      markerType: markerType,
      active: true
    };
  }

  private filterDepartments(
    departments: ClothingDepartmentMarkerOption[]
  ): ClothingDepartmentMarkerOption[] {
    return departments.filter((department) => this.isVisibleDepartment(department));
  }

  private isVisibleDepartment(department: ClothingDepartmentMarkerOption): boolean {
    if (!department || !department.id || !department.name) {
      return false;
    }
    return department.name.charAt(0) !== '[';
  }

  private syncMarkerSelection() {
    this.selectedDepartmentMarkerIndex = this.resolveDepartmentMarkerIndex();
    this.selectedUserMarkerIndex = this.resolveUserMarkerIndex();
    this.pendingDepartmentMarkerCode = '';
    this.pendingUserMarkerCode = '';
    this.markForCheck();
  }

  private resolveDepartmentMarkerIndex(): number {
    return this.resolveMarkerIndex(
      this.departmentAssignmentMarkers,
      this.pendingDepartmentMarkerCode,
      this.findFirstAssignedDepartmentMarkerCode(this.departmentAssignmentMarkers)
    );
  }

  private resolveUserMarkerIndex(): number {
    return this.resolveMarkerIndex(
      this.userAssignmentMarkers,
      this.pendingUserMarkerCode,
      this.findFirstAssignedUserMarkerCode(this.userAssignmentMarkers)
    );
  }

  private resolveMarkerIndex(
    markers: ClothingConfiguredMarker[],
    pendingMarkerCode: string,
    assignedMarkerCode: string
  ): number {
    if (!markers.length) {
      return 0;
    }
    if (pendingMarkerCode.length) {
      return this.findRequestedMarkerIndex(markers, pendingMarkerCode);
    }
    if (!assignedMarkerCode.length) {
      return 0;
    }
    return this.findRequestedMarkerIndex(markers, assignedMarkerCode);
  }

  private findFirstAssignedDepartmentMarkerCode(
    markers: ClothingConfiguredMarker[]
  ): string {
    var department = this.departments.find((item) => this.hasAssignedMarker(markers, item.departmentMarkerCode));

    if (!department) {
      return '';
    }
    return this.normalizeText(department.departmentMarkerCode);
  }

  private findFirstAssignedUserMarkerCode(
    markers: ClothingConfiguredMarker[]
  ): string {
    var user = this.users.find((item) => this.hasAssignedMarker(markers, item.jobMarkerCode));

    if (!user) {
      return '';
    }
    return this.normalizeText(user.jobMarkerCode);
  }

  private hasAssignedMarker(
    markers: ClothingConfiguredMarker[],
    markerCode?: string
  ): boolean {
    var normalizedMarkerCode = this.normalizeMarkerCode(markerCode || '');

    if (!normalizedMarkerCode.length) {
      return false;
    }
    return markers.some((marker) => this.isSameMarkerCode(marker.markerCode, normalizedMarkerCode));
  }

  private findRequestedMarkerIndex(
    markers: ClothingConfiguredMarker[],
    markerCode: string
  ): number {
    var normalizedMarkerCode = this.normalizeMarkerCode(markerCode);
    var markerIndex = markers.findIndex((marker) => this.isSameMarkerCode(marker.markerCode, normalizedMarkerCode));

    if (markerIndex !== -1) {
      return markerIndex;
    }
    return 0;
  }

  private resolveCurrentMarkerCode(
    markers: ClothingConfiguredMarker[],
    markerIndex: number
  ): string {
    if (!markers.length) {
      return '';
    }
    return markers[this.normalizeMarkerIndex(markers, markerIndex)].markerCode;
  }

  private moveMarkerIndex(
    markerIndex: number,
    markers: ClothingConfiguredMarker[],
    step: number
  ): number {
    if (!markers.length) {
      return 0;
    }
    return this.normalizeMarkerIndex(markers, markerIndex + step);
  }

  private normalizeMarkerIndex(
    markers: ClothingConfiguredMarker[],
    markerIndex: number
  ): number {
    var total = markers.length;

    if (!total) {
      return 0;
    }
    if (markerIndex < 0) {
      return total - 1;
    }
    if (markerIndex >= total) {
      return 0;
    }
    return markerIndex;
  }

  private mapDepartmentAssignmentItem(
    department: ClothingDepartmentMarkerOption
  ): ClothingMarkerAssignmentListItem {
    return {
      id: department.id,
      name: department.name,
      selected: this.isSameMarkerCode(department.departmentMarkerCode, this.currentDepartmentMarkerCode)
    };
  }

  private mapUserAssignmentItem(
    user: ClothingUsersListItem
  ): ClothingMarkerAssignmentListItem {
    return {
      id: user.id,
      name: user.fullName,
      selected: this.isSameMarkerCode(user.jobMarkerCode, this.currentUserMarkerCode)
    };
  }

  private isSameMarkerCode(leftMarkerCode?: string, rightMarkerCode?: string): boolean {
    return this.normalizeMarkerCode(leftMarkerCode || '') === this.normalizeMarkerCode(rightMarkerCode || '');
  }

  private normalizeMarkerCode(markerCode: string): string {
    return this.normalizeText(markerCode).toLowerCase();
  }

  private canSaveAssignment(markerCode: string): boolean {
    return markerCode.length > 0;
  }

  private resolveDepartmentAssignmentCode(departmentId: string): string {
    return this.resolveAssignmentMarkerCode(
      this.resolveDepartmentMarkerCode(departmentId),
      this.currentDepartmentMarkerCode
    );
  }

  private resolveUserAssignmentCode(userId: string): string {
    return this.resolveAssignmentMarkerCode(
      this.resolveUserMarkerCode(userId),
      this.currentUserMarkerCode
    );
  }

  private resolveAssignmentMarkerCode(currentMarkerCode: string, selectedMarkerCode: string): string {
    if (currentMarkerCode === selectedMarkerCode) {
      return '';
    }
    return selectedMarkerCode;
  }

  private resolveDepartmentMarkerCode(departmentId: string): string {
    var department = this.departments.find((item) => item.id === departmentId);

    if (!department) {
      return '';
    }
    return this.normalizeText(department.departmentMarkerCode);
  }

  private resolveUserMarkerCode(userId: string): string {
    var user = this.users.find((item) => item.id === userId);

    if (!user) {
      return '';
    }
    return this.normalizeText(user.jobMarkerCode);
  }

  private saveDepartmentAssignment(departmentId: string, markerCode: string) {
    this.clearMessages();
    this.isAssignmentSaving = true;
    this.adminService.updateGroupDepartmentMarker(departmentId, markerCode)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleDepartmentMarkerAssigned(departmentId, markerCode),
        (error: HttpErrorResponse) => this.handleAssignmentFailed(error)
      );
  }

  private saveUserAssignment(userId: string, markerCode: string) {
    this.clearMessages();
    this.isAssignmentSaving = true;
    this.loadAssignmentUserPayload(userId, markerCode)
      .switchMap((payload) => this.adminService.updateUser(payload))
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleUserMarkerAssigned(userId, markerCode),
        (error: HttpErrorResponse) => this.handleAssignmentFailed(error)
      );
  }

  private handleCreateMarkerResult(result: ClothingMarkerModalResult) {
    if (!result || !result.markerCode.length) {
      return;
    }
    this.clearMessages();
    this.isMarkerSaving = true;
    this.prepareConfiguredMarker(result)
      .takeUntil(this.destroy$)
      .subscribe(
        (marker) => this.handleMarkerCreated(marker, result),
        (error: HttpErrorResponse) => this.handleMarkerFailed(error)
      );
  }

  private prepareConfiguredMarker(
    result: ClothingMarkerModalResult
  ): Observable<ClothingConfiguredMarker> {
    var marker = this.findConfiguredMarker(result);

    if (!marker) {
      return this.clothingService.createConfiguredMarker(this.createMarkerPayload(result));
    }
    if (marker.active) {
      return Observable.of(marker);
    }
    return this.clothingService.updateConfiguredMarker(marker.id, this.createMarkerPayload(result));
  }

  private findConfiguredMarker(result: ClothingMarkerModalResult): ClothingConfiguredMarker {
    return this.configuredMarkers.find((marker) => this.isSameConfiguredMarker(marker, result));
  }

  private isSameConfiguredMarker(
    marker: ClothingConfiguredMarker,
    result: ClothingMarkerModalResult
  ): boolean {
    if (marker.markerType !== result.markerType) {
      return false;
    }
    return marker.markerCode === result.markerCode;
  }

  private handleMarkerCreated(
    marker: ClothingConfiguredMarker,
    result: ClothingMarkerModalResult
  ) {
    this.capturePendingMarker(result);
    this.applyCreatedMarker(marker);
    this.handleMarkerSaved('CLOTHING_MARKER_SAVED');
  }

  private applyCreatedMarker(marker: ClothingConfiguredMarker) {
    this.configuredMarkers = this.createConfiguredMarkersAfterSave(marker);
    this.markers = this.createMatrixMarkers(this.configuredMarkers);
    this.rows = this.createMatrixRows(this.catalogItems, this.markers, this.extractCurrentNorms());
  }

  private createConfiguredMarkersAfterSave(
    marker: ClothingConfiguredMarker
  ): ClothingConfiguredMarker[] {
    return this.sortConfiguredMarkers(this.upsertConfiguredMarker(this.configuredMarkers, marker));
  }

  private upsertConfiguredMarker(
    markers: ClothingConfiguredMarker[],
    marker: ClothingConfiguredMarker
  ): ClothingConfiguredMarker[] {
    var nextMarkers = markers.filter((item) => item.id !== marker.id);

    nextMarkers.push(marker);
    return nextMarkers;
  }

  private extractCurrentNorms(): ClothingMarkerNorm[] {
    var norms: ClothingMarkerNorm[] = [];

    this.rows.forEach((row) => this.collectRowNorms(row, norms));
    return norms;
  }

  private collectRowNorms(row: ClothingMatrixRow, norms: ClothingMarkerNorm[]) {
    row.cells
      .filter((cell) => this.hasSavedNorm(cell))
      .forEach((cell) => norms.push(this.createNormFromCell(row.item.id, cell)));
  }

  private hasSavedNorm(cell: ClothingMatrixCell): boolean {
    return cell.normId.length > 0;
  }

  private createNormFromCell(itemId: string, cell: ClothingMatrixCell): ClothingMarkerNorm {
    return {
      id: cell.normId,
      markerCode: cell.markerCode,
      markerType: cell.markerType,
      catalogItemId: itemId,
      quantity: cell.quantity,
      active: cell.active,
      createdAt: '',
      updatedAt: ''
    };
  }

  private capturePendingMarker(result: ClothingMarkerModalResult) {
    if (result.markerType === 'JOB') {
      this.pendingUserMarkerCode = result.markerCode;
      return;
    }
    this.pendingDepartmentMarkerCode = result.markerCode;
  }

  private handleMarkerSaved(successMessageKey: string) {
    this.isMarkerSaving = false;
    this.successMessageKey = successMessageKey;
    this.syncMarkerSelection();
  }

  private loadAssignmentUserPayload(
    userId: string,
    markerCode: string
  ): Observable<ClothingAssignmentUserPayload> {
    return Observable.forkJoin(
      this.userService.getUserFromAPI(userId),
      this.adminService.getUserInfo(userId)
    )
      .map((response) => this.createAssignmentUserPayload(response, markerCode));
  }

  private createAssignmentUserPayload(
    response: HttpResponse<any>[],
    markerCode: string
  ): ClothingAssignmentUserPayload {
    var userResponse = response[0];
    var infoResponse = response[1];
    var user = userResponse.body || {};
    var info = infoResponse.body || {};

    user.jobMarkerCode = markerCode;
    return {
      user: user,
      groupsWithPositions: info.groupsWithPositions || []
    };
  }

  private handleDepartmentMarkerAssigned(departmentId: string, markerCode: string) {
    this.departments = this.departments.map((department) => this.updateDepartmentMarker(department, departmentId, markerCode));
    this.handleAssignmentSaved(this.resolveDepartmentAssignmentMessage(markerCode));
  }

  private updateDepartmentMarker(
    department: ClothingDepartmentMarkerOption,
    departmentId: string,
    markerCode: string
  ): ClothingDepartmentMarkerOption {
    if (department.id !== departmentId) {
      return department;
    }
    return Object.assign({}, department, {
      departmentMarkerCode: markerCode
    });
  }

  private handleUserMarkerAssigned(userId: string, markerCode: string) {
    this.users = this.users.map((user) => this.updateUserMarker(user, userId, markerCode));
    this.handleAssignmentSaved(this.resolveUserAssignmentMessage(markerCode));
  }

  private resolveDepartmentAssignmentMessage(markerCode: string): string {
    if (!markerCode.length) {
      return 'CLOTHING_DEPARTMENT_MARKER_CLEARED';
    }
    return 'CLOTHING_DEPARTMENT_MARKER_SAVED';
  }

  private resolveUserAssignmentMessage(markerCode: string): string {
    if (!markerCode.length) {
      return 'CLOTHING_USER_MARKER_CLEARED';
    }
    return 'CLOTHING_USER_MARKER_SAVED';
  }

  private updateUserMarker(
    user: ClothingUsersListItem,
    userId: string,
    markerCode: string
  ): ClothingUsersListItem {
    if (user.id !== userId) {
      return user;
    }
    return Object.assign({}, user, {
      jobMarkerCode: markerCode
    });
  }

  private handleAssignmentSaved(successMessageKey: string) {
    this.isAssignmentSaving = false;
    this.successMessageKey = successMessageKey;
    this.markForCheck();
  }

  private handleAssignmentFailed(error: HttpErrorResponse) {
    this.isAssignmentSaving = false;
    this.showHttpError(error);
  }

  private createCatalogNameIndex(
    items: ClothingCatalogItemList[]
  ): ClothingCatalogNameIndex {
    var index: ClothingCatalogNameIndex = {};

    items.forEach((item) => index[item.id] = item.name);
    return index;
  }

  private normalizeText(value: string): string {
    if (!value) {
      return '';
    }
    return value.trim();
  }

  private normalizeNumber(value: number): number {
    var numberValue = Number(value);

    if (numberValue < 0 || isNaN(numberValue)) {
      return 0;
    }
    return numberValue;
  }

  private normalizePositiveNumber(value: number): number {
    var normalized = this.normalizeNumber(value);

    if (normalized < 1) {
      return 1;
    }
    return normalized;
  }

  private createCatalogModal() {
    return this.trackModalRef(this.modalService.open(ClothingCatalogModalComponent, {
      backdrop: 'static'
    }));
  }

  private handleCatalogItemLoaded(item: ClothingCatalogItem) {
    var modalRef = this.createCatalogModal();

    modalRef.componentInstance.setEditMode(item);
    this.handleCatalogModal(modalRef, item.id);
  }

  private handleCatalogModal(modalRef: any, itemId: string) {
    modalRef.result.then(
      (result: ClothingCatalogModalResult) => this.handleCatalogModalResult(result, itemId),
      () => null
    );
  }

  private handleCatalogModalResult(result: ClothingCatalogModalResult, itemId: string) {
    if (!result || !result.action) {
      return;
    }
    if (result.action === CLOTHING_CATALOG_MODAL_ACTIONS.save) {
      this.saveCatalogItem(itemId, result.payload);
      return;
    }
    if (itemId.length) {
      this.deactivateCatalogItem(itemId);
    }
  }

  private trackModalRef(modalRef: NgbModalRef): NgbModalRef {
    this.modalRefs.push(modalRef);
    modalRef.result.then(
      () => this.removeModalRef(modalRef),
      () => this.removeModalRef(modalRef)
    );
    return modalRef;
  }

  private removeModalRef(modalRef: NgbModalRef): void {
    this.modalRefs = this.modalRefs.filter(item => item !== modalRef);
  }

  private dismissOpenModals(): void {
    var refs = this.modalRefs.slice();
    this.modalRefs = [];
    refs.forEach(modalRef => this.dismissModal(modalRef));
  }

  private dismissModal(modalRef: NgbModalRef): void {
    try {
      modalRef.dismiss('clothing settings destroyed');
    } catch (error) {
      return;
    }
  }

  private saveCatalogItem(itemId: string, payload: ClothingCatalogItemPayload) {
    this.isCatalogSaving = true;
    this.isMatrixLoading = true;
    this.getCatalogSaveRequest(itemId, payload)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleCatalogSaved(),
        (error: HttpErrorResponse) => this.handleCatalogSaveFailed(error)
      );
  }

  private getCatalogSaveRequest(
    itemId: string,
    payload: ClothingCatalogItemPayload
  ): Observable<ClothingCatalogItem> {
    if (itemId.length) {
      return this.clothingService.updateCatalogItem(itemId, payload);
    }
    return this.clothingService.createCatalogItem(payload);
  }

  private deactivateCatalogItem(itemId: string) {
    this.isCatalogSaving = true;
    this.isMatrixLoading = true;
    this.clothingService.deactivateCatalogItem(itemId)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleCatalogDeactivated(),
        (error: HttpErrorResponse) => this.handleCatalogSaveFailed(error)
      );
  }

  private handleCatalogSaved() {
    this.isCatalogSaving = false;
    this.reloadMarkerData('CLOTHING_CATALOG_SAVED');
  }

  private handleCatalogDeactivated() {
    this.isCatalogSaving = false;
    this.reloadMarkerData('CLOTHING_ITEM_DEACTIVATED');
  }

  private handleCatalogSaveFailed(error: HttpErrorResponse) {
    this.isCatalogSaving = false;
    this.isMatrixLoading = false;
    this.showHttpError(error);
  }

  private getCellSaveRequest(
    itemId: string,
    cell: ClothingMatrixCell,
    quantity: number
  ): Observable<ClothingMarkerNorm> {
    if (!cell.normId.length) {
      return this.createCellNorm(itemId, cell, quantity);
    }
    return this.updateCellNorm(itemId, cell, quantity);
  }

  private createCellNorm(
    itemId: string,
    cell: ClothingMatrixCell,
    quantity: number
  ): Observable<ClothingMarkerNorm> {
    if (quantity <= 0) {
      return Observable.of(this.createZeroNorm(itemId, cell));
    }
    return this.clothingService.createNorm(this.buildCreateNormPayload(itemId, cell, quantity));
  }

  private updateCellNorm(
    itemId: string,
    cell: ClothingMatrixCell,
    quantity: number
  ): Observable<ClothingMarkerNorm> {
    return this.clothingService.updateNorm(
      cell.normId,
      this.buildUpdateNormPayload(itemId, cell, quantity)
    );
  }

  private buildCreateNormPayload(
    itemId: string,
    cell: ClothingMatrixCell,
    quantity: number
  ): ClothingMarkerNormPayload {
    return {
      markerCode: cell.markerCode,
      markerType: cell.markerType,
      catalogItemId: itemId,
      quantity: quantity
    };
  }

  private buildUpdateNormPayload(
    itemId: string,
    cell: ClothingMatrixCell,
    quantity: number
  ): ClothingMarkerNormPayload {
    return {
      markerCode: cell.markerCode,
      markerType: cell.markerType,
      catalogItemId: itemId,
      quantity: quantity,
      active: quantity > 0
    };
  }

  private createZeroNorm(itemId: string, cell: ClothingMatrixCell): ClothingMarkerNorm {
    return {
      id: '',
      markerCode: cell.markerCode,
      markerType: cell.markerType,
      catalogItemId: itemId,
      quantity: 0,
      active: false,
      createdAt: '',
      updatedAt: ''
    };
  }

  private handleCellSaved(cell: ClothingMatrixCell, norm: ClothingMarkerNorm) {
    cell.normId = this.resolveNormId(norm);
    cell.quantity = this.resolveCellQuantity(norm);
    cell.active = this.resolveNormActive(norm);
    this.successMessageKey = 'CLOTHING_NORM_SAVED';
    this.savingCellKey = '';
    this.cancelCellEdit();
  }

  private handleCellSaveFailed(error: HttpErrorResponse) {
    this.savingCellKey = '';
    this.showHttpError(error);
  }

  private clearMessages() {
    this.errorMessage = '';
    this.successMessageKey = '';
  }

  private showHttpError(error: HttpErrorResponse) {
    this.successMessageKey = '';
    this.errorMessage = this.resolveErrorMessage(error);
    this.markForCheck();
  }

  private resolveErrorMessage(error: HttpErrorResponse): string {
    return this.clothingErrorService.resolveDefaultMessage(error);
  }

  private markForCheck() {
    this.changeDetectorRef.markForCheck();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/clothing/clothing-settings.component.ts